import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { departmentAPI, ruleAPI, transactionAPI, beneficiaryAPI, userAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Layers, ArrowLeft, Wallet, CreditCard, Scale, History,
  BookUser, Edit, DollarSign, Loader2, Copy, CheckCircle, Zap, QrCode, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DepartmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState(null);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAllocateOpen, setIsAllocateOpen] = useState(false);
  const [isThresholdOpen, setIsThresholdOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', department_head_id: '' });
  const [allocateAmount, setAllocateAmount] = useState('');
  const [thresholdAmount, setThresholdAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [virtualAccountData, setVirtualAccountData] = useState(null);
  const [creatingVA, setCreatingVA] = useState(false);

  useEffect(() => {
    fetchData();
    fetchVirtualAccount();
  }, [id]);

  const fetchData = async () => {
    try {
      const [deptRes, statsRes, rulesRes, txnRes, benefRes, usersRes] = await Promise.all([
        departmentAPI.get(id),
        departmentAPI.getStats(id),
        ruleAPI.list(id),
        transactionAPI.list({ department_id: id, limit: 10 }),
        beneficiaryAPI.list(id),
        userAPI.list()
      ]);
      setDepartment(deptRes.data);
      setStats(statsRes.data);
      setRules(rulesRes.data);
      setTransactions(txnRes.data);
      setBeneficiaries(benefRes.data);
      setUsers(usersRes.data.filter(u => !u.department_id || u.user_id === deptRes.data.department_head_id));
      setEditData({
        name: deptRes.data.name,
        description: deptRes.data.description || '',
        department_head_id: deptRes.data.department_head_id || ''
      });
    } catch (err) {
      console.error('Failed to fetch department:', err);
      toast.error('Failed to load department details');
      navigate('/admin/departments');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await departmentAPI.update(id, editData);
      toast.success('Department updated');
      setIsEditOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAllocate = async () => {
    const amount = parseFloat(allocateAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await departmentAPI.allocateFunds(id, amount);
      toast.success(`$${amount.toLocaleString()} allocated successfully`);
      setIsAllocateOpen(false);
      setAllocateAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to allocate funds');
    } finally {
      setSaving(false);
    }
  };

  const handleSetThreshold = async () => {
    setSaving(true);
    try {
      // Convert to paise if provided, or null to disable
      const threshold = thresholdAmount ? parseFloat(thresholdAmount) * 100 : null;
      
      const res = await fetch(`${API_URL}/api/departments/${id}/auto-approval-threshold`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ auto_approval_threshold: threshold })
      });
      
      if (res.ok) {
        toast.success(threshold ? `Auto-approval threshold set to ₹${thresholdAmount}` : 'Auto-approval disabled');
        setIsThresholdOpen(false);
        setThresholdAmount('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update threshold');
      }
    } catch (err) {
      toast.error('Failed to update threshold');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const fetchVirtualAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/virtual-accounts?department_id=${id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const accounts = await res.json();
        if (accounts && accounts.length > 0) {
          setVirtualAccountData(accounts[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch virtual account:', err);
    }
  };

  const createVirtualAccount = async () => {
    if (!department) return;
    
    setCreatingVA(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/virtual-accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          department_id: id,
          department_name: department.name,
          description: `Collections for ${department.name}`
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Virtual account created successfully!');
        setVirtualAccountData(data);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create virtual account');
      }
    } catch (err) {
      toast.error('Failed to create virtual account');
    } finally {
      setCreatingVA(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="department-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/departments')} className="text-gray-500 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">{department?.name}</h1>
            <p className="text-gray-500">{department?.description || 'No description'}</p>
          </div>
          <Button variant="outline" onClick={() => setIsEditOpen(true)} className="border-gray-300 text-gray-600">
            <Edit className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button variant="outline" onClick={() => {
            setThresholdAmount(department?.auto_approval_threshold ? (department.auto_approval_threshold / 100).toString() : '');
            setIsThresholdOpen(true);
          }} className="border-green-300 text-green-700 hover:bg-green-50">
            <Zap className="w-4 h-4 mr-2" /> Auto-Approval
          </Button>
          <Button onClick={() => setIsAllocateOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <DollarSign className="w-4 h-4 mr-2" /> Allocate Funds
          </Button>
        </div>

        {/* Auto-Approval Threshold Info */}
        {department?.auto_approval_threshold && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Zap className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-green-800 font-medium">Auto-Approval Enabled</p>
                <p className="text-sm text-green-600">
                  Transactions up to ₹{(department.auto_approval_threshold / 100).toLocaleString()} are automatically approved without admin review.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Virtual Account Card */}
        {department?.virtual_account && (
          <Card className="virtual-account-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <CreditCard className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Virtual Account</h3>
                  <p className="text-sm text-gray-500">Use these details to receive payments</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Account Number</p>
                  <div className="flex items-center justify-between">
                    <p className="font-['JetBrains_Mono'] text-gray-900">{department.virtual_account.account_number}</p>
                    <button onClick={() => copyToClipboard(department.virtual_account.account_number)} className="text-gray-500 hover:text-gray-900">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">UPI ID</p>
                  <div className="flex items-center justify-between">
                    <p className="font-['JetBrains_Mono'] text-gray-900">{department.virtual_account.upi_id}</p>
                    <button onClick={() => copyToClipboard(department.virtual_account.upi_id)} className="text-gray-500 hover:text-gray-900">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                  <div className="flex items-center justify-between">
                    <p className="font-['JetBrains_Mono'] text-gray-900">{department.virtual_account.ifsc_code}</p>
                    <button onClick={() => copyToClipboard(department.virtual_account.ifsc_code)} className="text-gray-500 hover:text-gray-900">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Virtual Account Card (Unified Payments) */}
        {virtualAccountData ? (
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Virtual Account</h3>
                    <p className="text-sm text-gray-500">Receive payments via UPI and bank transfer</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                    {virtualAccountData.status || 'Active'}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {virtualAccountData.provider}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bank Account Details */}
                <div className="bg-white border rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Bank Account</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Account No:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{virtualAccountData.account_number}</span>
                        <button onClick={() => copyToClipboard(virtualAccountData.account_number)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">IFSC:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{virtualAccountData.ifsc_code}</span>
                        <button onClick={() => copyToClipboard(virtualAccountData.ifsc_code)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">{virtualAccountData.bank_name}</p>
                  </div>
                </div>
                
                {/* UPI ID */}
                {virtualAccountData.upi_id && (
                  <div className="bg-white border rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">UPI ID</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-lg text-gray-900">{virtualAccountData.upi_id}</span>
                      <button onClick={() => copyToClipboard(virtualAccountData.upi_id)} className="text-gray-400 hover:text-gray-600">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Scan & pay via any UPI app</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed border-2 border-gray-200 bg-gray-50/50">
            <CardContent className="p-6 text-center">
              <QrCode className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Enable Collections</h3>
              <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                Create a virtual account for this department to receive payments via UPI and bank transfer.
              </p>
              <Button 
                onClick={createVirtualAccount} 
                disabled={creatingVA}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creatingVA ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Virtual Account
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4">
              <Wallet className="w-5 h-5 text-blue-600 mb-2" />
              <p className="text-xs text-gray-500">Balance</p>
              <p className="text-lg font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(department?.balance)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <History className="w-5 h-5 text-blue-500 mb-2" />
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-lg font-bold text-gray-900">{stats?.completed_transactions || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <Scale className="w-5 h-5 text-amber-500 mb-2" />
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-lg font-bold text-gray-900">{stats?.pending_transactions || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <DollarSign className="w-5 h-5 text-purple-500 mb-2" />
              <p className="text-xs text-gray-500">Total Spent</p>
              <p className="text-lg font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(stats?.total_spent)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <BookUser className="w-5 h-5 text-pink-500 mb-2" />
              <p className="text-xs text-gray-500">Beneficiaries</p>
              <p className="text-lg font-bold text-gray-900">{stats?.beneficiary_count || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="transactions" className="data-[state=active]:bg-blue-900/10 data-[state=active]:text-blue-600">Recent Transactions</TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-blue-900/10 data-[state=active]:text-blue-600">Rules ({rules.length})</TabsTrigger>
            <TabsTrigger value="beneficiaries" className="data-[state=active]:bg-blue-900/10 data-[state=active]:text-blue-600">Beneficiaries ({beneficiaries.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="transactions" className="mt-4">
            <Card className="glass-card">
              <CardContent className="p-0">
                {transactions.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                        <th className="p-4">Beneficiary</th>
                        <th className="p-4">Amount</th>
                        <th className="p-4">Status</th>
                        <th className="p-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.transaction_id} className="border-b border-gray-200/50">
                          <td className="p-4 text-gray-900">{txn.beneficiary_name}</td>
                          <td className="p-4 font-['JetBrains_Mono'] text-gray-900">{formatCurrency(txn.amount)}</td>
                          <td className="p-4"><Badge className={`status-${txn.status === 'COMPLETED' ? 'success' : txn.status === 'PENDING_APPROVAL' ? 'pending' : 'error'}`}>{txn.status}</Badge></td>
                          <td className="p-4 text-gray-500 text-sm">{new Date(txn.submitted_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-gray-500">No transactions yet</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="rules" className="mt-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                {rules.length > 0 ? (
                  <div className="space-y-3">
                    {rules.map((rule) => (
                      <div key={rule.rule_id} className="flex items-center justify-between p-4 bg-white/50 rounded-lg">
                        <div>
                          <p className="text-gray-900 font-medium">{rule.rule_type.replace('_', ' ')}</p>
                          <p className="text-sm text-gray-500">{rule.period ? `${rule.period} limit` : 'Total allocation'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-['JetBrains_Mono'] text-gray-900">{formatCurrency(rule.amount)}</p>
                          <p className="text-xs text-gray-500">Used: {formatCurrency(rule.current_usage)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">No rules configured</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="beneficiaries" className="mt-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                {beneficiaries.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {beneficiaries.map((b) => (
                      <div key={b.beneficiary_id} className="flex items-center justify-between p-4 bg-white/50 rounded-lg">
                        <div>
                          <p className="text-gray-900 font-medium">{b.name}</p>
                          <p className="text-sm text-gray-500 font-['JetBrains_Mono']">{b.upi_id || b.account_number}</p>
                        </div>
                        <Badge className={b.is_blacklisted ? 'status-error' : 'status-success'}>
                          {b.is_blacklisted ? 'Blacklisted' : 'Active'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">No beneficiaries added</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Edit Department</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Name</Label>
                <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-gray-50 border-gray-300 text-gray-900" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Description</Label>
                <Input value={editData.description} onChange={(e) => setEditData({...editData, description: e.target.value})} className="bg-gray-50 border-gray-300 text-gray-900" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Department Head</Label>
                <Select value={editData.department_head_id} onValueChange={(val) => setEditData({...editData, department_head_id: val})}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-gray-50 border-gray-300">
                    <SelectItem value="none" className="text-gray-500">None</SelectItem>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id} className="text-gray-900">{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleEdit} className="w-full bg-blue-900 hover:bg-blue-800" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Allocate Dialog */}
        <Dialog open={isAllocateOpen} onOpenChange={setIsAllocateOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Allocate Funds</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-gray-500 text-sm">Current Balance: <span className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(department?.balance)}</span></p>
              <div className="space-y-2">
                <Label className="text-gray-500">Amount to Allocate</Label>
                <Input
                  type="number"
                  value={allocateAmount}
                  onChange={(e) => setAllocateAmount(e.target.value)}
                  placeholder="10000"
                  className="bg-gray-50 border-gray-300 text-gray-900 font-['JetBrains_Mono']"
                />
              </div>
              <Button onClick={handleAllocate} className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Allocate Funds
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Auto-Approval Threshold Dialog */}
        <Dialog open={isThresholdOpen} onOpenChange={setIsThresholdOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                Auto-Approval Threshold
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-gray-500 text-sm">
                Set an amount below which transactions are <strong>automatically approved</strong> without admin review.
                Leave empty to require approval for all transactions.
              </p>
              
              {department?.auto_approval_threshold && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    Current threshold: <strong>₹{(department.auto_approval_threshold / 100).toLocaleString()}</strong>
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-gray-600">Threshold Amount (₹)</Label>
                <Input
                  type="number"
                  value={thresholdAmount}
                  onChange={(e) => setThresholdAmount(e.target.value)}
                  placeholder="5000 (leave empty to disable)"
                  className="bg-white border-gray-300 text-gray-900 font-['JetBrains_Mono']"
                  data-testid="threshold-input"
                />
                <p className="text-xs text-gray-500">
                  Example: Enter 5000 to auto-approve transactions up to ₹5,000
                </p>
              </div>
              
              <div className="flex gap-3">
                {department?.auto_approval_threshold && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setThresholdAmount('');
                      handleSetThreshold();
                    }} 
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    disabled={saving}
                  >
                    Disable Auto-Approval
                  </Button>
                )}
                <Button 
                  onClick={handleSetThreshold} 
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white" 
                  disabled={saving}
                  data-testid="save-threshold-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />} 
                  {thresholdAmount ? 'Set Threshold' : 'Disable'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DepartmentDetail;
